import { Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { SoftCard } from '@/components/ui/soft-card';

/**
 * Stock upload history chart — uploaded vs in-stock barcodes per day over
 * the last 30 days. `data` rows are `{ date, totalUploaded, inStock }`.
 */
export function StockUploadHistory({ data }) {
	// The most recent day that actually has data — not the last slot, which is
	// `null` until today's daily snapshot lands (otherwise the header reads 0).
	const last = [...data].reverse().find((d) => d.inStock != null || d.totalUploaded != null) ?? null;

	return (
		<SoftCard className="!p-5 md:!p-6">
			<div className="mb-1 flex items-baseline justify-between">
				<h3 className="text-[15px] font-bold text-foreground md:text-base">Stock upload history</h3>
				<span className="text-[11px] text-muted-foreground">Last 30 days</span>
			</div>
			{last ? (
				<p className="text-sm text-muted-foreground">
					<span className="mr-3 inline-flex items-center gap-1.5">
						<span className="h-2 w-2 rounded-full bg-success" />
						<span className="font-semibold tabular-nums text-foreground">{(last.totalUploaded ?? 0).toLocaleString()}</span>
						<span>uploaded</span>
					</span>
					<span className="inline-flex items-center gap-1.5">
						<span className="h-2 w-2 rounded-full bg-primary" />
						<span className="font-semibold tabular-nums text-foreground">{(last.inStock ?? 0).toLocaleString()}</span>
						<span>in stock</span>
					</span>
				</p>
			) : (
				<p className="text-sm text-muted-foreground">No upload history yet.</p>
			)}
			<div className="mt-3 h-32 w-full">
				<ResponsiveContainer width="100%" height="100%">
					<LineChart data={data} margin={{ top: 5, right: 8, left: 0, bottom: 0 }}>
						<XAxis
							dataKey="date"
							tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
							tickLine={false}
							axisLine={false}
							interval={6}
						/>
						<YAxis hide domain={['dataMin - 200', 'dataMax + 200']} />
						<Tooltip
							contentStyle={{
								background: 'hsl(var(--popover))',
								border: '1px solid hsl(var(--border))',
								borderRadius: 8,
								fontSize: 12
							}}
						/>
						<Line
							type="monotone"
							dataKey="totalUploaded"
							stroke="hsl(var(--success))"
							strokeWidth={2}
							dot={false}
							connectNulls
							name="Uploaded"
						/>
						<Line
							type="monotone"
							dataKey="inStock"
							stroke="hsl(var(--primary))"
							strokeWidth={2}
							dot={false}
							connectNulls
							name="In stock"
						/>
					</LineChart>
				</ResponsiveContainer>
			</div>
		</SoftCard>
	);
}
